import { noteExcerpt } from "@/utils/content"
import { extractHashtags, extractMentions, extractEventReferences } from "@/utils/content"
import { nowSeconds } from "@/utils/time"
import { KINDS, tagValue, type NostrEvent, type NostrFilter } from "../types"

/** Kinds rendered in a relay feed. */
export const FEED_KINDS = [KINDS.NOTE, KINDS.REPOST, KINDS.ARTICLE]

/**
 * Post types this client renders. The order is the order of the filter UI.
 *
 * `note` and `reply` share kind 1: the difference is the NIP-10 reply markers,
 * which relays cannot filter on, so replies are separated client side.
 */
export const POST_TYPES = ["note", "reply", "repost", "article"] as const

export type PostType = (typeof POST_TYPES)[number]

/** Every post type — the "all types" selection. */
export const ALL_POST_TYPES: readonly PostType[] = POST_TYPES

/** Classifies an event into the post type the UI renders it as. */
export function postTypeOf(event: NostrEvent): PostType | undefined {
  switch (event.kind) {
    case KINDS.REPOST:
      return "repost"
    case KINDS.ARTICLE:
      return "article"
    case KINDS.NOTE:
      return isReply(event) ? "reply" : "note"
    default:
      return undefined
  }
}

/** Relay kinds needed to fetch a selection (notes and replies share kind 1). */
export function kindsForPostTypes(types: readonly PostType[] | undefined): number[] {
  const kinds = new Set<number>()
  for (const type of types ?? []) {
    if (type === "note" || type === "reply") kinds.add(KINDS.NOTE)
    else if (type === "repost") kinds.add(KINDS.REPOST)
    else if (type === "article") kinds.add(KINDS.ARTICLE)
  }
  return kinds.size > 0 ? [...kinds] : [...FEED_KINDS]
}

/** True when the event matches the selection; an empty or complete selection matches everything. */
export function matchesPostTypes(event: NostrEvent, types?: readonly PostType[]): boolean {
  if (!types || types.length === 0 || types.length >= POST_TYPES.length) return true
  const type = postTypeOf(event)
  return type !== undefined && types.includes(type)
}

/** Filters a list by post type; returns the same array when everything is selected. */
export function filterByPostTypes<T extends NostrEvent>(events: T[], types?: readonly PostType[]): T[] {
  if (!types || types.length === 0 || types.length >= POST_TYPES.length) return events
  return events.filter((event) => matchesPostTypes(event, types))
}


export interface FeedQuery {
  relayUrls: string[]
  authors?: string[]
  limit?: number
  /** Upper bound on `created_at` (pagination cursor or a custom end date). */
  until?: number
  /** Lower bound on `created_at` (time range filter). */
  since?: number
  search?: string
  /** Include replies (notes with an `e` tag). Defaults to false for readability. */
  includeReplies?: boolean
}

/**
 * Builds the NIP-01 filters for a relay feed.
 *
 * Replies are excluded from the main feed with a separate `#e` guard: NIP-01
 * cannot express "no e tag", so filtering happens client side in `filterFeed`.
 * `since` / `until` / `authors` / `kinds` are the parts of the unified filter
 * architecture that relays can apply themselves.
 */
export function buildFeedFilters(query: FeedQuery): NostrFilter[] {
  const limit = query.limit ?? 40
  const filter: NostrFilter = {
    kinds: FEED_KINDS,
    limit,
  }
  if (query.authors && query.authors.length > 0) filter.authors = query.authors
  if (query.until) filter.until = query.until
  if (query.since) filter.since = query.since
  if (query.search) filter.search = query.search
  return [filter]
}

const REPLY_MARKERS = new Set(["root", "reply"])

/** True when a kind 1 event is a reply rather than a root note. */
export function isReply(event: NostrEvent): boolean {
  if (event.kind !== KINDS.NOTE) return false
  const eTags = event.tags.filter((tag) => tag[0] === "e")
  if (eTags.length === 0) return false
  return eTags.some((tag) => !tag[3] || REPLY_MARKERS.has(tag[3]))
}

/** Root (`root`) or direct parent (`reply`) id of a NIP-10 note. */
export function replyTargets(event: NostrEvent): { rootId?: string; replyId?: string } {
  const eTags = event.tags.filter((tag) => tag[0] === "e" && tag[1])
  const root = eTags.find((tag) => tag[3] === "root")?.[1] ?? eTags[0]?.[1]
  const reply = eTags.find((tag) => tag[3] === "reply")?.[1] ?? eTags[eTags.length - 1]?.[1]
  return { rootId: root, replyId: reply }
}

/** NIP-10 style ordering: newest first, de-duplicated by id. */
export function sortEvents(events: NostrEvent[]): NostrEvent[] {
  return [...events].sort((a, b) => {
    if (b.created_at !== a.created_at) return b.created_at - a.created_at
    return a.id.localeCompare(b.id)
  })
}

export function dedupeEvents(events: NostrEvent[]): NostrEvent[] {
  const seen = new Set<string>()
  const result: NostrEvent[] = []
  for (const event of events) {
    if (seen.has(event.id)) continue
    seen.add(event.id)
    result.push(event)
  }
  return result
}

/** Client side feed filtering (post type, replies, muted words, hidden events). */
export function filterFeed(
  events: NostrEvent[],
  options: {
    includeReplies?: boolean
    /** Post types to keep; omitted, empty or complete means everything. */
    postTypes?: readonly PostType[]
    mutedWords?: string[]
    hiddenPubkeys?: Set<string>
    hiddenEventIds?: Set<string>
  } = {},
): NostrEvent[] {
  const mutedWords = (options.mutedWords ?? []).map((word) => word.toLowerCase()).filter(Boolean)
  return events.filter((event) => {
    if (options.hiddenEventIds?.has(event.id)) return false
    if (options.hiddenPubkeys?.has(event.pubkey)) return false
    if (!options.includeReplies && isReply(event)) return false
    if (!matchesPostTypes(event, options.postTypes)) return false
    if (mutedWords.length > 0) {
      const haystack = event.content.toLowerCase()
      if (mutedWords.some((word) => haystack.includes(word))) return false
    }
    return true
  })
}

/** Oldest `created_at` of a page, used as the `until` cursor for pagination. */
export function nextUntil(events: NostrEvent[]): number | undefined {
  if (events.length === 0) return undefined
  return Math.min(...events.map((event) => event.created_at)) - 1
}

/** Tags required when publishing a note or a reply. */
export function buildNoteTags(options: {
  content: string
  replyTo?: NostrEvent
  root?: NostrEvent
  relayHint?: string
}): string[][] {
  const tags: string[][] = []
  const { replyTo, root } = options

  if (replyTo) {
    const rootEvent = root ?? replyTo
    if (rootEvent.id !== replyTo.id) {
      tags.push(["e", rootEvent.id, options.relayHint ?? "", "root", rootEvent.pubkey])
    } else {
      tags.push(["e", replyTo.id, options.relayHint ?? "", "root", replyTo.pubkey])
    }
    if (rootEvent.id !== replyTo.id) {
      tags.push(["e", replyTo.id, options.relayHint ?? "", "reply", replyTo.pubkey])
    }
    const parentAuthors = new Set([replyTo.pubkey, ...(root ? [root.pubkey] : [])])
    for (const pubkey of parentAuthors) tags.push(["p", pubkey])
    for (const pubkey of extractMentions(options.content)) {
      if (!parentAuthors.has(pubkey)) tags.push(["p", pubkey])
    }
    // Carry the root's community / hashtag tags so replies stay discoverable.
    for (const tag of rootEvent.tags.filter((item) => item[0] === "t" || item[0] === "a").slice(0, 6)) {
      tags.push([...tag])
    }
  } else {
    for (const pubkey of extractMentions(options.content)) tags.push(["p", pubkey])
  }

  const referenced = extractEventReferences(options.content)
  for (const id of referenced) {
    if (!tags.some((tag) => tag[0] === "e" && tag[1] === id)) tags.push(["e", id, "", "mention"])
  }

  for (const hashtag of extractHashtags(options.content)) tags.push(["t", hashtag])
  return tags
}

/** Adds the configured client tag (`["client", name]`, NIP-89 style). */
export function withClientTag(tags: string[][], clientName: string): string[][] {
  if (tags.some((tag) => tag[0] === "client")) return tags
  return [...tags, ["client", clientName]]
}

export function noteSummary(event: NostrEvent, max = 180): string {
  return noteExcerpt(event.content, max)
}

export function noteTitle(event: NostrEvent): string | undefined {
  return tagValue(event, "title") ?? tagValue(event, "subject")
}

export function feedTimestamp(): number {
  return nowSeconds()
}
